/* Horizontal scroll rail. The track scrolls natively (touch, trackpad, scroll-snap),
   can be dragged with a mouse, and the optional prev/next buttons step it by one item.
   Buttons are disabled at the ends, and both are hidden when everything already fits. */
class OjRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    this.track.addEventListener('pointerdown', this.onDragStart);
    this.track.addEventListener('click', this.onClickCapture, true);

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  onDragStart = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.dragX = event.clientX;
    this.dragLeft = this.track.scrollLeft;
    this.dragged = false;
    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragEnd, { once: true });
  };

  onDragMove = (event) => {
    const delta = event.clientX - this.dragX;
    if (!this.dragged && Math.abs(delta) < 6) return;
    if (!this.dragged) {
      this.dragged = true;
      this.track.classList.add('is-dragging');
    }
    this.track.scrollLeft = this.dragLeft - delta;
  };

  onDragEnd = () => {
    window.removeEventListener('pointermove', this.onDragMove);
    this.track.classList.remove('is-dragging');
    // The click that ends a drag fires before this timer, so it is still swallowed.
    setTimeout(() => {
      this.dragged = false;
    }, 0);
  };

  /* A drag shouldn't also follow the link it started on. */
  onClickCapture = (event) => {
    if (!this.dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragged = false;
  };

  step(direction) {
    const item = this.track.firstElementChild;
    if (!item) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = item.getBoundingClientRect().width + gap;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: direction * distance, behavior: reduced ? 'auto' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflow = scrollWidth - clientWidth > 1;
    this.toggleAttribute('data-overflow', overflow);
    if (this.prev) this.prev.disabled = !overflow || scrollLeft <= 1;
    if (this.next) this.next.disabled = !overflow || scrollLeft + clientWidth >= scrollWidth - 1;
  }
}

if (!customElements.get('oj-rail')) customElements.define('oj-rail', OjRail);
